import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../apiConfig.js';

export default function AddUser() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    user_type: 'secondary',
    job_perm: 'read',
    invoice_perm: 'read',
    price_perm: 'visible',
    quote_perm: 'read',
    customer_perm: 'read'
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAddUser = async () => {
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      alert('Please fill in all required fields.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('userToken');
      
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        // Note: No password field - this will trigger the add-user flow
      };

      console.log('🚀 Adding user with data:', userData);

      const response = await axios.post(`${API_BASE_URL}/create-account`, userData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('✅ User creation response:', response.data);

      if (response.data.code === 'ACCOUNT_CREATED') {
        alert(`User Added Successfully! ✅\n\nAn invitation email has been sent to ${formData.email}. They will receive a link to set their password and complete their account setup.`);
        navigate('/users');
      } else if (response.data.code === 'ALREADY_REGISTERED') {
        alert('Email Already Registered: This email address is already associated with an account.');
      } else if (response.data.code === 'PENDING_CONFIRMATION') {
        alert('Email Pending Confirmation: This email address is already registered but not yet confirmed.');
      } else {
        alert('Success: User has been added to your account.');
        navigate('/users');
      }
    } catch (error) {
      console.error('Error adding user:', error);
      console.error('User creation error response:', error.response?.data);
      
      let errorMessage = 'Failed to add user. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid user data. Please check all fields.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. You do not have access to add users.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  return (
    <div style={styles.scrollContainer}>
      <div style={styles.container}>
        <h1 style={styles.header}>Add User</h1>
        <form style={styles.form}>
          <label style={styles.label}>First Name</label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            style={styles.input}
          />

          <label style={styles.label}>Last Name</label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            style={styles.input}
          />

          <label style={styles.label}>Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            style={styles.input}
          />

          <label style={styles.label}>Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            style={styles.input}
          />

          <label style={styles.label}>User Type</label>
          <select
            value={formData.user_type}
            onChange={(e) => handleChange('user_type', e.target.value)}
            style={styles.select}
          >
            <option value="admin">Admin</option>
            <option value="secondary">Secondary</option>
          </select>

          <label style={styles.label}>Job Permissions</label>
          <select
            value={formData.job_perm}
            onChange={(e) => handleChange('job_perm', e.target.value)}
            style={styles.select}
          >
            <option value="none">None</option>
            <option value="read">Read</option>
            <option value="write">Write</option>
          </select>

          <label style={styles.label}>Invoice Permissions</label>
          <select
            value={formData.invoice_perm}
            onChange={(e) => handleChange('invoice_perm', e.target.value)}
            style={styles.select}
          >
            <option value="none">None</option>
            <option value="read">Read</option>
            <option value="write">Write</option>
          </select>

          <label style={styles.label}>Price Permissions</label>
          <select
            value={formData.price_perm}
            onChange={(e) => handleChange('price_perm', e.target.value)}
            style={styles.select}
          >
            <option value="visible">Visible</option>
            <option value="hidden">Hidden</option>
          </select>

          <label style={styles.label}>Quote Permissions</label>
          <select
            value={formData.quote_perm}
            onChange={(e) => handleChange('quote_perm', e.target.value)}
            style={styles.select}
          >
            <option value="none">None</option>
            <option value="read">Read</option>
            <option value="write">Write</option>
          </select>

          <label style={styles.label}>Customer Permissions</label>
          <select
            value={formData.customer_perm}
            onChange={(e) => handleChange('customer_perm', e.target.value)}
            style={styles.select}
          >
            <option value="none">None</option>
            <option value="read">Read</option>
            <option value="write">Write</option>
          </select>

          <div style={styles.buttonContainer}>
            <button type="button" onClick={handleAddUser} style={styles.addButton} disabled={loading}>
              {loading ? 'Adding...' : 'Add User'}
            </button>
            <button type="button" onClick={handleCancel} style={styles.cancelButton} disabled={loading}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  scrollContainer: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    padding: '20px 0',
    overflowY: 'auto',
  },
  container: {
    maxWidth: '500px',
    margin: '0 auto',
    padding: '30px',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
    color: '#333',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#555',
  },
  input: {
    marginBottom: '15px',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '16px',
  },
  select: {
    marginBottom: '15px',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '16px',
    backgroundColor: '#fff',
  },
  buttonContainer: {
    display: 'flex',
    gap: '15px',
    marginTop: '30px',
  },
  addButton: {
    flex: 1,
    backgroundColor: '#007BFF',
    color: '#fff',
    padding: '10px 18px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
    ':hover': {
      backgroundColor: '#0056b3',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(0, 123, 255, 0.4)',
    },
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'red',
    color: '#fff',
    padding: '10px 18px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(108, 117, 125, 0.3)',
    ':hover': {
      backgroundColor: '#545b62',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 16px rgba(108, 117, 125, 0.4)',
    },
  },
};